import { useState } from "react";
import KpiCard, { KpiRow, KpiMore } from "@/components/KpiCard";
import MobileStatusBanner from "@/components/MobileStatusBanner";
import { useIsMobile } from "@/hooks/useMediaQuery";
import InfoTooltip from "@/components/InfoTooltip";
import ChartFrame from "@/components/ChartFrame";
import ToggleGroup from "@/components/ToggleGroup";
import { endpointDot } from "@/lib/chartStyle";
import { fmt, toolLabel } from "@/lib/format";
import { ComposedChart, Area, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import Panel from "../Panel";
import { Collapse, allDates, rebucketDaily, deltaPct } from "./lib";

// 계기판 세그먼트 (2026-07-20 분해) — 상태 배너(판정 결론) → 히어로 4장 →
// 접힌 세부 지표 → 추이(일/주/월·누적) → 도구별 배수 → 상위 지출 → 읽는 법.
function BoardSegment({ d, pv, range, spanDays }) {
  const [boardGran, setBoardGran] = useState("day");
  const isMobile = useIsMobile();
  const [boardCum, setBoardCum] = useState(false);
  const [spendersOpen, setSpendersOpen] = useState(false);

  const t = d.totals || {};
  const cov = d.openaiCoverage;
  const cache = d.cache;

  const seriesByDate = Object.fromEntries((d.series || []).map((r) => [r.date, r]));
  const boardDaily = allDates(range.from, range.to).map((date) => ({
    date,
    costKrw: seriesByDate[date]?.costKrw ?? 0,
    deductedNc: seriesByDate[date]?.deductedNc ?? 0,
  }));
  let boardSeries = rebucketDaily(boardDaily, boardGran);
  if (boardCum) {
    let c = 0, n = 0;
    boardSeries = boardSeries.map((r) => ({ ...r, costKrw: (c += r.costKrw), deductedNc: (n += r.deductedNc) }));
  }

  return (
    <>
      {(() => {
        // 상태 배너 — 공통 MobileStatusBanner 로 추출(2026-09-21). 문구·판정·보조문구는
        // 종전과 동일하게 유지한다(회귀 금지). level/문구 계산만 여기서 하고 렌더는 공통 컴포넌트.
        const alerts = d.alerts || [];
        const worst = alerts.some((a) => a.level === "warn") ? "warn"
                    : alerts.some((a) => a.level === "info") ? "info" : "ok";
        const headline = {
          warn: `확인 필요 ${alerts.filter((a) => a.level === "warn").length}건 — 아래 항목을 조치하세요`,
          info: `정상 — 참고 ${alerts.filter((a) => a.level === "info").length}건 (조치 필수 아님)`,
          ok: "오늘은 확인할 것 없습니다 — 정상",
        }[worst];
        return (
          <MobileStatusBanner
            domain="finops"
            level={worst}
            headline={headline}
            hint="매일 아침 여기까지만 보면 됩니다 — 10초"
            messages={alerts.filter((a) => a.level !== "ok").map((a) => a.message)}
          />
        );
      })()}

      <KpiRow cols={4} mobileLayout="hero">
        <KpiCard label="마진 배수" value={d.multiples?.nominal ?? "—"} tone="brand" hero mobileHero
                 sublabel="차감 ÷ 모델원가 · 설계값 4.0 · 3.5 미만이면 누수" />
        <KpiCard label={`모델원가 · ${spanDays}일 합계`}
                 value={isMobile ? fmt.krwCompact(t.costKrw ?? 0) : fmt.krw(t.costKrw ?? 0)}
                 valueTitle={isMobile ? fmt.krw(t.costKrw ?? 0) : undefined} tone="warn" invert deltaBelow={isMobile}
                 deltaPct={deltaPct(t.costKrw, pv?.costKrw)}
                 sublabel={`조회 기간(${range.from.slice(5)}~${range.to.slice(5)}) 합계 · 차감 ${fmt.nc(t.deductedNc ?? 0)}크레딧`} />
        <KpiCard label={`결제액 · ${spanDays}일 합계`}
                 value={isMobile ? fmt.krwCompact(t.revenueKrw ?? 0) : fmt.krw(t.revenueKrw ?? 0)}
                 valueTitle={isMobile ? fmt.krw(t.revenueKrw ?? 0) : undefined} tone="good" deltaBelow={isMobile}
                 deltaPct={deltaPct(t.revenueKrw, pv?.revenueKrw)}
                 sublabel="조회 기간 토스 실결제 (수기 지급 제외)" />
        {/* 캐시 히트율은 참고 지표 — 모바일 첫 화면에서는 아래 '상세 지표 보기'로 내린다. */}
        {!isMobile && (
          <KpiCard label="캐시 히트율"
                   value={cache?.hitRatePct != null ? `${cache.hitRatePct}%` : "—"} tone="good"
                   sublabel={cache ? `절감 ${fmt.krw(cache.savedKrw)} · 캐싱 투자의 성과` : "데이터 없음"} />
        )}
      </KpiRow>
      {isMobile && (
        <KpiMore label="상세 지표 보기">
          <KpiRow cols={3} mobileLayout="hero">
            <KpiCard label="캐시 히트율"
                     value={cache?.hitRatePct != null ? `${cache.hitRatePct}%` : "—"} tone="good"
                     sublabel={cache ? `절감 ${fmt.krw(cache.savedKrw)} · 캐싱 투자의 성과` : "데이터 없음"} />
          </KpiRow>
        </KpiMore>
      )}

      {/* 세부 지표 — 매일 볼 필요 없는 8장은 접어 둔다. 그룹 라벨이 성격을 말한다. */}
      <details className="group rounded-2xl bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.08)]">
        <summary className="flex cursor-pointer items-center justify-between px-4 py-3.5 text-sm font-semibold text-ink-800 [&::-webkit-details-marker]:hidden">
          세부 지표 8개
          <span className="text-[12px] font-normal text-ink-400 group-open:hidden">펼치기 — 마진 상세 · 원장 맥락 · 계측 상태</span>
          <span className="hidden text-[12px] font-normal text-ink-400 group-open:inline">접기</span>
        </summary>
        <div className="space-y-4 px-4 pb-4">
          <div>
            <p className="mb-2 text-[12px] font-medium uppercase tracking-wide text-ink-400">마진 상세</p>
            <KpiRow cols={3}>
              <KpiCard label="실효 배수 (프로모 반영)" value={d.multiples?.effectivePromo ?? "—"}
                       sublabel={d.promoX2 ? "충전 ×2 반영 · 9/9 종료 시 마진 배수로 복귀" : "프로모 종료 — 마진 배수와 동일"} />
              <KpiCard label="메시지 1건 처리 원가" value={t.costPerUsage != null ? fmt.krw(t.costPerUsage) : "—"}
                       tone="warn" invert
                       deltaPct={deltaPct(t.costPerUsage, pv?.costPerUsage)}
                       sublabel={`요청 ${fmt.int(t.usageCount ?? 0)}건 기준 · 단위경제 핵심`} />
              <KpiCard label="매출 대비 모델원가" value={t.costToRevenuePct != null ? `${t.costToRevenuePct}%` : "—"}
                       tone="warn"
                       sublabel={t.costToRevenuePct > 100
                         ? "지금은 무상 크레딧 소진 단계 — 100% 초과가 정상입니다"
                         : `결제 ${fmt.int(t.chargeCount ?? 0)}건 ${fmt.krw(t.revenueKrw ?? 0)} 기준`} />
            </KpiRow>
          </div>
          <div>
            <p className="mb-2 text-[12px] font-medium uppercase tracking-wide text-ink-400">원장 맥락</p>
            <KpiRow cols={3}>
              <KpiCard label="운영비 (수기 등록)" value={fmt.krw(t.opexKrw ?? 0)} tone="warn"
                       sublabel="Route53 등 인프라 실비 — 매출·마진 추이 탭과 동일 원장" />
              <KpiCard label="무상 지급 크레딧" value={fmt.nc(t.freeGrantNc ?? 0)}
                       sublabel="수기 지급(admin_adjust) — 차감의 상당분이 이 소진분" />
              <KpiCard label="잔액 소진 차단" value={`${fmt.int(t.blockedCount ?? 0)}건`}
                       tone={t.blockedCount ? "warn" : "default"}
                       sublabel="차단 = 충전 전환 기회 — 리텐션 탭 퍼널 참조" />
            </KpiRow>
          </div>
          <div>
            <p className="mb-2 text-[12px] font-medium uppercase tracking-wide text-ink-400">계측 상태</p>
            <KpiRow cols={3}>
              <KpiCard label="OpenAI 커버리지"
                       value={cov?.coveragePct != null ? `${cov.coveragePct}%` : "—"}
                       tone={(cov?.coveragePct ?? 100) >= 90 ? "default" : "warn"}
                       sublabel={cov ? `실지출 $${cov.actualUsd} vs 원장 $${cov.trackedUsd} · 90% 미만=미계측` : "Costs API 조회 실패"} />
              <KpiCard label="원가 미기록 호출" value={`${fmt.int(t.untrackedCalls ?? 0)}건`}
                       tone={t.untrackedCalls ? "warn" : "default"}
                       sublabel="문체 분석 — 토큰 미기록(계측 백로그)" />
              <KpiCard label="요청 수" value={fmt.int(t.usageCount ?? 0)}
                       sublabel="이 기간 원가 발생 호출 (스펙 호출 포함)" />
            </KpiRow>
          </div>
        </div>
      </details>

      {/* ② 추이 — 스파이크는 모양으로 잡는다. 일/주/월·누적 토글(964줄 분해 때 누락됐던 컨트롤 복원). */}
      <Panel title="모델원가·차감 추이"
             action={
               <div className="flex flex-wrap items-center gap-2">
                 <ToggleGroup options={[{ id: "day", label: "일별" }, { id: "week", label: "주별" }, { id: "month", label: "월별" }]}
                              value={boardGran} onChange={setBoardGran} />
                 <ToggleGroup options={[{ id: false, label: "구간" }, { id: true, label: "누적" }]}
                              value={boardCum} onChange={setBoardCum} />
               </div>
             }>
        <ChartFrame base="h-52 sm:h-64" tall="h-80 sm:h-[28rem]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={boardSeries} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="finDeduct" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563eb" stopOpacity={0.18} />
                  <stop offset="95%" stopColor="#2563eb" stopOpacity={0.01} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="#f2f2f0" />
              <XAxis dataKey="date" tickFormatter={(v) => (boardGran === "month" ? v : v.slice(5))} minTickGap={28}
                     tick={{ fill: "#9a9a96", fontSize: 11 }} axisLine={false} tickLine={false} tickMargin={10} />
              <YAxis tick={{ fill: "#9a9a96", fontSize: 11 }} tickFormatter={fmt.compact} width={44}
                     axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ borderRadius: 14, border: "none", boxShadow: "0 6px 20px rgba(15,23,42,0.14)", fontSize: 12, padding: "8px 12px" }}
                formatter={(v, name) => [name === "모델원가" ? fmt.krw(v) : `${fmt.nc(v)} 크레딧`, name]} />
              <Legend verticalAlign="top" height={28} iconType="circle" iconSize={8}
                      wrapperStyle={{ fontSize: 12, color: "#6b6b68", paddingBottom: 6 }} />
              {/* 차감(면적, 위) vs 모델원가(선, 아래)의 간격 = 마진. 색은 차분하게(로즈 → 앰버). */}
              <Area type="natural" dataKey="deductedNc" name="차감 크레딧" stroke="#2563eb" strokeWidth={2.5}
                    fill="url(#finDeduct)" dot={endpointDot("#2563eb", boardSeries.length)} activeDot={{ r: 4 }} />
              <Line type="natural" dataKey="costKrw" name="모델원가" stroke="#f59e0b" strokeWidth={2.25}
                    dot={endpointDot("#f59e0b", boardSeries.length)} activeDot={{ r: 4 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartFrame>
      </Panel>

      {/* ③ 어디가 새는가 */}
      <Panel title="도구별 배수 (낮은 순)"
             action={<InfoTooltip text="종량 도구는 자동으로 4.0. 정액 도구(이미지 생성)가 조용히 깨지는 걸 잡는 감시 행 — 3.5 미만 빨간색이면 정액 조정 검토. 이미지 스펙 호출(imagegen_spec, 차감 0·원가만)은 별도 행 — 이미지 생성과 합쳐 보면 실마진. 건당 원가는 가격 설계의 기초값." />}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[480px] text-[13px]">
            <thead>
              <tr className="text-left text-ink-400">
                <th className="pb-2 font-medium">도구</th>
                <th className="pb-2 text-right font-medium">건수</th>
                <th className="pb-2 text-right font-medium">건당 원가</th>
                <th className="pb-2 text-right font-medium">모델원가</th>
                <th className="pb-2 text-right font-medium">차감 크레딧</th>
                <th className="pb-2 text-right font-medium">배수</th>
              </tr>
            </thead>
            <tbody>
              {(d.toolMultiples || []).map((tm) => (
                <tr key={tm.tool} className="border-t border-ink-100">
                  <td className="py-1.5">{toolLabel(tm.tool)}</td>
                  <td className="py-1.5 text-right tabular-nums">{fmt.int(tm.n)}</td>
                  <td className="py-1.5 text-right tabular-nums">{tm.n ? fmt.krw(tm.costKrw / tm.n) : "—"}</td>
                  <td className="py-1.5 text-right tabular-nums">{fmt.krw(tm.costKrw)}</td>
                  <td className="py-1.5 text-right tabular-nums">{fmt.nc(tm.deductedNc)}</td>
                  <td className={`py-1.5 text-right font-semibold tabular-nums ${tm.multiple != null && tm.multiple < 3.5 ? "text-red-600" : "text-ink-700"}`}>
                    {tm.multiple ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel title="상위 지출 사용자"
             action={<InfoTooltip text="기간 원가 기준 상위 30명. 많이 쓰는 사용자 = 이상 사용 점검 대상이자 B2B 영업 후보. 모델원가는 공시단가 기반 산출." />}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[420px] text-[13px]">
            <thead>
              <tr className="text-left text-ink-400">
                <th className="pb-2 font-medium">사용자</th>
                <th className="pb-2 text-right font-medium">모델원가</th>
                <th className="pb-2 text-right font-medium">차감 크레딧</th>
                <th className="pb-2 text-right font-medium">배수</th>
              </tr>
            </thead>
            <tbody>
              {(d.topSpenders || []).slice(0, spendersOpen ? 30 : 10).map((u) => (
                <tr key={u.userId} className="border-t border-ink-100">
                  <td className="py-2 text-[12.5px]">
                    {u.email
                      ? u.email
                      : <span className="font-mono text-[12px] text-ink-400">{u.userId.slice(0, 20)}… (익명)</span>}
                  </td>
                  <td className="py-2 text-right tabular-nums">{fmt.krw(u.costKrw)}</td>
                  <td className="py-2 text-right tabular-nums">{fmt.nc(u.deductedNc)}</td>
                  <td className="py-2 text-right tabular-nums">{u.costKrw ? (u.deductedNc / u.costKrw).toFixed(2) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(d.topSpenders || []).length > 10 && (
            <button type="button" onClick={() => setSpendersOpen((v) => !v)}
                    className="mt-2 w-full rounded-lg bg-ink-50 py-2 text-[12.5px] font-medium text-ink-500 hover:bg-ink-100">
              {spendersOpen ? "접기" : `더보기 (${(d.topSpenders || []).length - 10}명)`}
            </button>
          )}
        </div>
      </Panel>

      {/* 계기판 읽는 법 — 튜토리얼(2026-07-20 피드백: "어떻게 보는 건지 친절하게") */}
      <Collapse title="이 화면 읽는 법 (처음이라면 펼쳐 보세요)">
        <ol className="list-decimal space-y-2 pl-5 text-[13px] leading-relaxed text-ink-600">
          <li><b className="text-ink-800">상태 배너</b> — 색이 곧 결론입니다. 초록 = 오늘 볼 것 없음, 파랑 = 참고만(조치 불필요), 노랑 = 조치 필요. 매일 아침 여기까지 10초.</li>
          <li><b className="text-ink-800">카드 4장</b> — 마진 배수는 "설계값 4.0이 유지되는가"(3.5 미만이면 어딘가 샘). 모델원가·결제액은 위에서 고른 조회 기간의 합계고, 증감 배지는 직전 동일 길이 기간과 비교한 값입니다.</li>
          <li><b className="text-ink-800">세부 지표 8개</b> — 매일 볼 필요 없는 맥락 지표. "무상 지급 크레딧"이 큰 동안은 차감 ≠ 매출이고, "원가 미기록 호출"은 아직 계측 안 되는 호출(문체 분석)의 개수입니다.</li>
          <li><b className="text-ink-800">추이 차트</b> — 파란 선(차감)과 빨간 선(원가)의 간격이 마진입니다. 두 선이 가까워지면 누수 신호. 주별·월별·누적으로 바꿔 볼 수 있습니다.</li>
          <li><b className="text-ink-800">도구별 배수</b> — 4.0에서 떨어진 도구가 범인입니다. "이미지 생성"과 "이미지 스펙 호출(내부)"은 한 세트로 합쳐 해석하세요.</li>
          <li><b className="text-ink-800">상위 지출 사용자</b> — 기준(기간 원가 3만원)을 넘으면 배너에 올라옵니다. 이상 사용 점검 대상이자, 정상 고객이면 B2B 영업 후보라는 이중 의미.</li>
        </ol>
      </Collapse>

    </>
  );
}

export default BoardSegment;
